import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/adminApi';
import './EventForm.css';

const EventForm = ({ event, cities, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    event_type: '',
    event_date: '',
    end_date: '',
    city: '',
    location: '',
    latitude: '',
    longitude: '',
    max_participants: '',
    priority: 'medium',
    status: 'active',
    notes: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const isEditMode = !!event;

  // Initialize form data
  useEffect(() => {
    if (event) {
      setFormData({
        name: event.name || '',
        description: event.description || '',
        event_type: event.event_type || '',
        event_date: event.event_date ? new Date(event.event_date).toISOString().slice(0, 16) : '',
        end_date: event.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : '',
        city: event.city || '',
        location: event.location || '',
        latitude: event.latitude || '',
        longitude: event.longitude || '',
        max_participants: event.max_participants || '',
        priority: event.priority || 'medium',
        status: event.status || 'active',
        notes: event.notes || ''
      });
    }
  }, [event]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Event adı zorunludur';
    }
    if (!formData.event_type) {
      newErrors.event_type = 'Event türü zorunludur';
    }
    if (!formData.event_date) {
      newErrors.event_date = 'Event tarihi zorunludur';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'Şehir zorunludur';
    }

    // Date validation
    if (formData.event_date) {
      const eventDate = new Date(formData.event_date);
      const now = new Date();
      if (eventDate < now) {
        newErrors.event_date = 'Event tarihi gelecekte olmalıdır';
      }
    }

    // End date validation
    if (formData.end_date && formData.event_date) {
      const eventDate = new Date(formData.event_date);
      const endDate = new Date(formData.end_date);
      if (endDate <= eventDate) {
        newErrors.end_date = 'Bitiş tarihi başlangıç tarihinden sonra olmalıdır';
      }
    }

    // Max participants validation
    if (formData.max_participants && (isNaN(formData.max_participants) || parseInt(formData.max_participants) <= 0)) {
      newErrors.max_participants = 'Geçerli bir katılımcı sayısı giriniz';
    }

    // Coordinates validation
    if (formData.latitude && (isNaN(formData.latitude) || Math.abs(formData.latitude) > 90)) {
      newErrors.latitude = 'Geçerli bir enlem giriniz (-90 ile 90 arası)';
    }
    if (formData.longitude && (isNaN(formData.longitude) || Math.abs(formData.longitude) > 180)) {
      newErrors.longitude = 'Geçerli bir boylam giriniz (-180 ile 180 arası)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare data
      const submitData = { ...formData };
      
      // Convert coordinates to numbers if provided
      if (submitData.latitude) {
        submitData.latitude = parseFloat(submitData.latitude);
      }
      if (submitData.longitude) {
        submitData.longitude = parseFloat(submitData.longitude);
      }
      if (submitData.max_participants) {
        submitData.max_participants = parseInt(submitData.max_participants);
      }

      await onSubmit(submitData);
    } catch (error) {
      console.error('Form submission error:', error);
      // Handle API errors
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ 
          general: error.response?.data?.message || 'Bir hata oluştu' 
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const eventTypes = [
    { value: '', label: 'Event türü seçiniz' },
    { value: 'concert', label: '🎵 Konser' },
    { value: 'sports', label: '⚽ Spor Etkinliği' },
    { value: 'conference', label: '🎤 Konferans' },
    { value: 'festival', label: '🎪 Festival' },
    { value: 'exhibition', label: '🎨 Sergi' },
    { value: 'theater', label: '🎭 Tiyatro' },
    { value: 'cinema', label: '🎬 Sinema' },
    { value: 'nightlife', label: '🌙 Gece Hayatı' },
    { value: 'education', label: '📚 Eğitim' },
    { value: 'business', label: '💼 İş' },
    { value: 'other', label: '📅 Diğer' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Düşük', color: '#10b981' },
    { value: 'medium', label: 'Orta', color: '#f59e0b' },
    { value: 'high', label: 'Yüksek', color: '#dc2626' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Aktif', color: '#10b981' },
    { value: 'draft', label: 'Taslak', color: '#6b7280' },
    { value: 'completed', label: 'Tamamlandı', color: '#3b82f6' },
    { value: 'cancelled', label: 'İptal Edildi', color: '#ef4444' }
  ];

  const handleLocationSearch = () => {
    // Simple location search implementation
    // In a real app, you'd integrate with a map service
    setShowLocationPicker(!showLocationPicker);
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString()
          }));
          setShowLocationPicker(false);
        },
        (error) => {
          alert('Konum alınamadı: ' + error.message);
        }
      );
    } else {
      alert('Tarayıcınız konum servisini desteklemiyor');
    }
  };

  return (
    <div className="event-form">
      <div className="form-header">
        <h3>
          {isEditMode ? '✏️ Event Düzenle' : '➕ Yeni Event Ekle'}
        </h3>
        <p className="form-subtitle">
          {isEditMode 
            ? `${event.name} bilgilerini güncelleyin`
            : 'Yeni event bilgilerini doldurun'
          }
        </p>
      </div>

      {errors.general && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit} className="event-form-content">
        {/* Basic Information */}
        <div className="form-section">
          <h4 className="section-title">📅 Temel Bilgiler</h4>
          <div className="form-grid">
            <div className="form-group full-width">
              <label htmlFor="name">
                Event Adı <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={errors.name ? 'error' : ''}
                placeholder="Event adını giriniz"
                disabled={isSubmitting}
              />
              {errors.name && (
                <span className="error-text">{errors.name}</span>
              )}
            </div>

            <div className="form-group full-width">
              <label htmlFor="description">
                Açıklama
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className={errors.description ? 'error' : ''}
                placeholder="Event hakkında detaylı bilgi..."
                rows="3"
                disabled={isSubmitting}
              />
              {errors.description && (
                <span className="error-text">{errors.description}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="event_type">
                Event Türü <span className="required">*</span>
              </label>
              <select
                id="event_type"
                name="event_type"
                value={formData.event_type}
                onChange={handleInputChange}
                className={errors.event_type ? 'error' : ''}
                disabled={isSubmitting}
              >
                {eventTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.event_type && (
                <span className="error-text">{errors.event_type}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="status">
                Durum <span className="required">*</span>
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className={errors.status ? 'error' : ''}
                disabled={isSubmitting}
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.status && (
                <span className="error-text">{errors.status}</span>
              )}
            </div>
          </div>
        </div>

        {/* Date & Time Information */}
        <div className="form-section">
          <h4 className="section-title">⏰ Tarih & Saat Bilgileri</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="event_date">
                Başlangıç Tarihi <span className="required">*</span>
              </label>
              <input
                type="datetime-local"
                id="event_date"
                name="event_date"
                value={formData.event_date}
                onChange={handleInputChange}
                className={errors.event_date ? 'error' : ''}
                disabled={isSubmitting}
              />
              {errors.event_date && (
                <span className="error-text">{errors.event_date}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="end_date">
                Bitiş Tarihi
              </label>
              <input
                type="datetime-local"
                id="end_date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                className={errors.end_date ? 'error' : ''}
                disabled={isSubmitting}
              />
              {errors.end_date && (
                <span className="error-text">{errors.end_date}</span>
              )}
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div className="form-section">
          <h4 className="section-title">📍 Lokasyon Bilgileri</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="city">
                Şehir <span className="required">*</span>
              </label>
              <select
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className={errors.city ? 'error' : ''}
                disabled={isSubmitting}
              >
                <option value="">Şehir seçiniz</option>
                {cities.map(city => (
                  <option key={city.city} value={city.city}>
                    {city.city}
                  </option>
                ))}
              </select>
              {errors.city && (
                <span className="error-text">{errors.city}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="location">
                Adres/Mekan
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className={errors.location ? 'error' : ''}
                placeholder="Event mekanı/adresi"
                disabled={isSubmitting}
              />
              {errors.location && (
                <span className="error-text">{errors.location}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="latitude">
                Enlem (Latitude)
              </label>
              <div className="coordinate-input">
                <input
                  type="number"
                  id="latitude"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleInputChange}
                  className={errors.latitude ? 'error' : ''}
                  placeholder="41.0082"
                  step="any"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="location-btn"
                  onClick={handleLocationSearch}
                  disabled={isSubmitting}
                  title="Konum Seç"
                >
                  📍
                </button>
              </div>
              {errors.latitude && (
                <span className="error-text">{errors.latitude}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="longitude">
                Boylam (Longitude)
              </label>
              <div className="coordinate-input">
                <input
                  type="number"
                  id="longitude"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleInputChange}
                  className={errors.longitude ? 'error' : ''}
                  placeholder="28.9784"
                  step="any"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  className="current-location-btn"
                  onClick={handleUseCurrentLocation}
                  disabled={isSubmitting}
                  title="Mevcut Konumu Kullan"
                >
                  🧭
                </button>
              </div>
              {errors.longitude && (
                <span className="error-text">{errors.longitude}</span>
              )}
            </div>
          </div>

          {showLocationPicker && (
            <div className="location-picker">
              <p>🗺️ Gelişmiş konum seçici yakında eklenecek...</p>
              <button 
                type="button" 
                onClick={() => setShowLocationPicker(false)}
                className="btn-small"
              >
                Kapat
              </button>
            </div>
          )}
        </div>

        {/* Event Details */}
        <div className="form-section">
          <h4 className="section-title">⚙️ Event Detayları</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="max_participants">
                Maksimum Katılımcı
              </label>
              <input
                type="number"
                id="max_participants"
                name="max_participants"
                value={formData.max_participants}
                onChange={handleInputChange}
                className={errors.max_participants ? 'error' : ''}
                placeholder="Katılımcı sayısı"
                min="1"
                disabled={isSubmitting}
              />
              {errors.max_participants && (
                <span className="error-text">{errors.max_participants}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="priority">
                Öncelik
              </label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className={errors.priority ? 'error' : ''}
                disabled={isSubmitting}
              >
                {priorityOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.priority && (
                <span className="error-text">{errors.priority}</span>
              )}
            </div>

            <div className="form-group full-width">
              <label htmlFor="notes">
                Notlar
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className={errors.notes ? 'error' : ''}
                placeholder="Event hakkında ek notlar..."
                rows="3"
                disabled={isSubmitting}
              />
              {errors.notes && (
                <span className="error-text">{errors.notes}</span>
              )}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            ❌ İptal
          </button>
          <button
            type="submit"
            className="btn-submit"
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? (
              <>
                <span className="loading-spinner"></span>
                {isEditMode ? 'Güncelleniyor...' : 'Ekleniyor...'}
              </>
            ) : (
              <>
                {isEditMode ? '💾 Güncelle' : '➕ Ekle'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventForm;
